import {
  endOfDay,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { useMemo } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { loadAdminPreferences } from "@/lib/adminSettings";

export type PeriodPreset = "today" | "yesterday" | "7d" | "14d" | "30d" | "90d" | "6m" | "1y" | "all" | "custom";

interface DateRange {
  startDate: Date | null;
  endDate: Date;
}

export interface PeriodFilterValue extends DateRange {
  period: PeriodPreset;
  previousStartDate: Date | null;
  previousEndDate: Date | null;
  label: string;
  setPeriod: (nextPeriod: PeriodPreset, customRange?: { from: Date; to: Date }) => void;
}

const labels: Record<PeriodPreset, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  "7d": "7 dias",
  "14d": "14 dias",
  "30d": "30 dias",
  "90d": "90 dias",
  "6m": "6 meses",
  "1y": "1 ano",
  all: "Todo periodo",
  custom: "Personalizado",
};

function parseDate(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getCurrentRange(period: PeriodPreset, from?: Date | null, to?: Date | null): DateRange {
  const now = new Date();

  switch (period) {
    case "today":
      return { startDate: startOfDay(now), endDate: now };
    case "yesterday": {
      const yesterday = subDays(now, 1);
      return { startDate: startOfDay(yesterday), endDate: endOfDay(yesterday) };
    }
    case "7d":
      return { startDate: subDays(now, 7), endDate: now };
    case "14d":
      return { startDate: subDays(now, 14), endDate: now };
    case "30d":
      return { startDate: subDays(now, 30), endDate: now };
    case "90d":
      return { startDate: subDays(now, 90), endDate: now };
    case "6m":
      return { startDate: subMonths(now, 6), endDate: now };
    case "1y":
      return { startDate: subYears(now, 1), endDate: now };
    case "all":
      return { startDate: null, endDate: now };
    case "custom":
      return {
        startDate: from ? startOfDay(from) : startOfMonth(now),
        endDate: to ? endOfDay(to) : endOfYear(now),
      };
    default:
      return { startDate: startOfYear(now), endDate: now };
  }
}

function getPreviousRange(range: DateRange) {
  if (!range.startDate) {
    return { previousStartDate: null, previousEndDate: null };
  }

  const durationMs = range.endDate.getTime() - range.startDate.getTime();
  const previousEndDate = new Date(range.startDate.getTime() - 1);
  const previousStartDate = new Date(previousEndDate.getTime() - durationMs);

  return { previousStartDate, previousEndDate };
}

export function usePeriodFilter(): PeriodFilterValue {
  const location = useRouterState({ select: (state) => state.location });
  const navigate = useNavigate();

  const search = useMemo(() => (location.search as Record<string, unknown>) ?? {}, [location.search]);
  const defaultPeriod = loadAdminPreferences().defaultPeriod;
  const rawPeriod = typeof search.period === "string" ? search.period : defaultPeriod;
  const period = (["today", "yesterday", "7d", "14d", "30d", "90d", "6m", "1y", "all", "custom"] as const).includes(
    rawPeriod as PeriodPreset
  )
    ? (rawPeriod as PeriodPreset)
    : "30d";

  const from = parseDate(search.from);
  const to = parseDate(search.to);
  const range = getCurrentRange(period, from, to);
  const previous = getPreviousRange(range);

  return useMemo(
    () => ({
      period,
      startDate: range.startDate,
      endDate: range.endDate,
      previousStartDate: previous.previousStartDate,
      previousEndDate: previous.previousEndDate,
      label: labels[period],
      setPeriod: (nextPeriod, customRange) => {
        const nextSearch = { ...search } as Record<string, unknown>;
        nextSearch.period = nextPeriod;

        if (nextPeriod === "custom" && customRange) {
          nextSearch.from = format(customRange.from, "yyyy-MM-dd");
          nextSearch.to = format(customRange.to, "yyyy-MM-dd");
        } else {
          delete nextSearch.from;
          delete nextSearch.to;
        }

        navigate({
          to: location.pathname,
          search: nextSearch,
          replace: true,
        }).catch(() => undefined);
      },
    }),
    [location.pathname, navigate, period, previous.previousEndDate, previous.previousStartDate, range.endDate, range.startDate, search]
  );
}
